import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Loader2, ShieldCheck, FileText, CheckCircle2, AlertTriangle } from "lucide-react";
import { formatCurrency } from "@/lib/api";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function PublicQuoteAccept() {
  const { token } = useParams();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API_BASE}/api/public/quotes/accept/${token}`);
        if (!mounted) return;
        setQuote(data);
        if (data.already_accepted) setDone(true);
      } catch (e) {
        if (!mounted) return;
        setError(e?.response?.data?.detail || "Link non valido o scaduto");
      } finally { if (mounted) setLoading(false); }
    })();
    return () => { mounted = false; };
  }, [token]);

  const accept = async () => {
    setAccepting(true);
    try {
      await axios.post(`${API_BASE}/api/public/quotes/accept/${token}`);
      setDone(true);
    } catch (e) { setError(e?.response?.data?.detail || "Errore durante l'accettazione"); }
    finally { setAccepting(false); }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50">
      <Loader2 className="h-10 w-10 animate-spin text-blue-600" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-br from-rose-50 to-amber-50">
      <div className="bg-white border border-rose-200 rounded-xl shadow-lg p-8 max-w-md text-center">
        <AlertTriangle className="h-12 w-12 text-rose-600 mx-auto mb-3" />
        <h1 className="font-display text-2xl font-semibold text-slate-900">Link non valido</h1>
        <p className="text-slate-600 mt-2">{error}</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6 flex items-center justify-center" data-testid="public-quote-accept-page">
      <div className="bg-white border border-slate-200 rounded-xl shadow-xl p-8 max-w-lg w-full">
        <div className="text-center mb-6">
          <div className="h-16 w-16 mx-auto rounded-full bg-blue-100 text-blue-700 flex items-center justify-center mb-3">
            <FileText className="h-8 w-8" />
          </div>
          <p className="text-xs uppercase tracking-[0.2em] text-blue-600 mb-1">Preventivo</p>
          <h1 className="font-display text-3xl font-semibold text-slate-900">n° {quote?.quote_number}</h1>
          {quote?.client_name && <p className="text-slate-500 mt-1">Per: <strong>{quote.client_name}</strong></p>}
        </div>

        <div className="space-y-2 bg-slate-50 border border-slate-200 rounded-lg p-4 text-sm">
          <div className="flex justify-between"><span className="text-slate-500">Ramo</span><span className="font-medium capitalize">{quote?.type?.replace(/_/g, " ")}</span></div>
          {quote?.estimated_premium != null && (
            <div className="flex justify-between"><span className="text-slate-500">Premio stimato</span><span className="font-display font-semibold text-lg text-emerald-700 tabular-nums">{formatCurrency(quote.estimated_premium)}</span></div>
          )}
          {quote?.coverage_request && (
            <div className="mt-2 pt-2 border-t border-slate-200">
              <p className="text-slate-500 text-xs mb-1">Garanzie richieste</p>
              <p className="text-slate-700 whitespace-pre-line">{quote.coverage_request}</p>
            </div>
          )}
        </div>

        {done ? (
          <div className="mt-6 bg-emerald-50 border border-emerald-200 rounded-lg p-5 text-center" data-testid="quote-accepted-confirmation">
            <CheckCircle2 className="h-12 w-12 text-emerald-600 mx-auto mb-2" />
            <p className="font-display text-xl font-semibold text-emerald-900">Preventivo accettato</p>
            <p className="text-sm text-emerald-700 mt-1">L&apos;agenzia ha ricevuto la conferma. Verrai contattato a breve.</p>
          </div>
        ) : (
          <>
            <Button
              data-testid="public-accept-btn"
              onClick={accept}
              disabled={accepting}
              className="w-full mt-6 bg-emerald-600 hover:bg-emerald-700 text-white text-base font-semibold py-6 rounded-full shadow-lg"
            >
              {accepting ? <><Loader2 className="h-5 w-5 animate-spin mr-2" /> Conferma in corso…</> : <><ShieldCheck className="h-5 w-5 mr-2" /> Accetto il preventivo</>}
            </Button>
            <p className="text-xs text-slate-500 text-center mt-3">Cliccando &quot;Accetto&quot; autorizzi l&apos;agenzia a procedere con la stipula della polizza.</p>
          </>
        )}
      </div>
    </div>
  );
}
